import type { CreativesSurface } from '@alison/analytics';
import { AnalyticsService } from '@alison/analytics/client';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { CustomizeMetrics } from './CustomizeMetrics.js';
import {
  type Aggregation,
  type TopMetric,
  type TopMetricsScope,
  useCreativeScoreTile,
  useMetricCatalog,
  useTopMetrics,
} from './data/api.js';
import { CREATIVE_SCORE_METRIC_NAME } from './data/displayedMetrics.js';
import {
  MIN_TOP_METRICS,
  type PickableMetric,
  pruneSelection,
  ticketMetrics,
} from './data/selection.js';
import { MetricTile } from './MetricTile.js';
import { MetricTrendDialog } from './MetricTrendDialog.js';

const KPI_RELATIVE_METRIC_NAMES = new Set([
  'potential_savings',
  'potential_uplift',
  'spend_on_top_performers',
  'spend_on_underperformers',
  CREATIVE_SCORE_METRIC_NAME,
]);

type CreativeScope = Omit<TopMetricsScope, 'kpis'>;

interface TopMetricsStripProps {
  accountIds: number[];
  dateWindow: [string, string];
  filters: Record<string, string[]> | undefined;
  chosen: string[];
  onChosenChange: (chosen: string[]) => void;
  onSelectionChange: (selection: string[]) => void;
  leadingKpi: string;
  surface: CreativesSurface;
}

export function TopMetricsStrip({
  accountIds,
  dateWindow,
  filters,
  chosen,
  onChosenChange,
  onSelectionChange,
  leadingKpi,
  surface,
}: TopMetricsStripProps): ReactNode {
  const [aggregations, setAggregations] = useState<Record<string, Aggregation>>({});
  const [trendMetric, setTrendMetric] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const { data: catalog, error: catalogError } = useMetricCatalog(accountIds);

  const pickable = useMemo<PickableMetric[]>(
    () =>
      ticketMetrics(Object.keys(catalog ?? {})).map((name) => ({
        name,
        label: catalog?.[name]?.label ?? name,
      })),
    [catalog],
  );

  const selection = useMemo(() => {
    const names = pickable.map((metric) => metric.name);
    const pruned = new Set(pruneSelection(chosen, names));
    return names.filter((name) => pruned.has(name));
  }, [chosen, pickable]);

  useEffect(() => {
    onSelectionChange(selection);
  }, [selection, onSelectionChange]);

  const scope = useMemo<CreativeScope>(
    () => ({
      dateRange: dateWindow,
      leadingKpi,
      ...(accountIds.length ? { accountIds } : {}),
      ...(filters ? { filters } : {}),
    }),
    [accountIds, dateWindow, filters, leadingKpi],
  );

  const wantsCreativeScore = selection.includes(CREATIVE_SCORE_METRIC_NAME);
  const topMetricsKpis = useMemo(
    () => selection.filter((name) => name !== CREATIVE_SCORE_METRIC_NAME),
    [selection],
  );

  const topMetrics = useTopMetrics(
    { ...scope, kpis: topMetricsKpis },
    selection.length >= MIN_TOP_METRICS,
  );
  const creativeScoreTile = useCreativeScoreTile(
    scope,
    accountIds.length > 0 && wantsCreativeScore,
  );
  const failure = catalogError ?? topMetrics.error ?? creativeScoreTile.error;
  const loaded = topMetrics.data;

  const byName = useMemo(() => {
    const map = new Map<string, TopMetric>(loaded?.metrics.map((metric) => [metric.name, metric]));
    if (creativeScoreTile.data !== undefined) {
      map.set(CREATIVE_SCORE_METRIC_NAME, creativeScoreTile.data);
    }
    return map;
  }, [loaded, creativeScoreTile.data]);

  const openTrend = trendMetric === null ? undefined : byName.get(trendMetric);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        px: 6,
        pt: 2,
        pb: collapsed ? 2 : 1,
      }}
    >
      {!collapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <CustomizeMetrics
            metrics={pickable}
            selected={selection}
            onChange={onChosenChange}
            surface={surface}
          />
        </Box>
      )}

      {failure && <Alert severity="error">{failure.message}</Alert>}

      {catalog && pickable.length < MIN_TOP_METRICS && (
        <Alert severity="info">
          The selected accounts report too few of the overview metrics to fill the strip.
        </Alert>
      )}

      <Box
        id="top-metrics-tiles"
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(196px, 1fr))',
          gap: 2,
          overflow: collapsed ? 'hidden' : 'visible',
          mb: collapsed ? 0 : 1,
          maxHeight: collapsed ? 0 : 300,
          opacity: collapsed ? 0 : 1,
          transition: theme.transitions.create(['max-height', 'opacity', 'margin-bottom'], {
            duration: theme.transitions.duration.shorter,
          }),
        })}
      >
        {loaded
          ? selection.map((name) => {
              const metric = byName.get(name);
              if (metric === undefined) {
                return <Skeleton key={name} variant="rounded" height={123} />;
              }
              return (
                <MetricTile
                  key={name}
                  metric={metric}
                  currencySymbol={loaded.currencySymbol}
                  averageable={metric.average !== null}
                  aggregation={aggregations[name] ?? 'total'}
                  description={catalog?.[name]?.description ?? metric.label}
                  onShowTrend={() => {
                    AnalyticsService.track('Top Metrics - Open Graph', { surface, metric: name });
                    setTrendMetric(name);
                  }}
                  onAggregationChange={(aggregation) => {
                    AnalyticsService.track('Top Metrics - Change Aggregation', {
                      surface,
                      metric: name,
                      aggregation,
                    });
                    setAggregations((current) => ({ ...current, [name]: aggregation }));
                  }}
                  {...(KPI_RELATIVE_METRIC_NAMES.has(name) ? { flashToken: leadingKpi } : {})}
                />
              );
            })
          : failure === null &&
            selection.map((name) => <Skeleton key={name} variant="rounded" height={123} />)}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        aria-controls="top-metrics-tiles"
        aria-label={collapsed ? 'Expand summary' : 'Collapse summary'}
        data-test="top-metrics-collapse"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mx: 'auto',
          width: 'fit-content',
          minWidth: 52,
          height: 20,
          border: 0,
          p: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
          color: 'text.secondary',
          transition: theme.transitions.create('color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': { color: 'primary.main' },
          '& svg': {
            transform: collapsed ? 'none' : 'rotate(180deg)',
            transition: theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          },
        })}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
        {collapsed && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            See top metrics summary
          </Box>
        )}
      </Box>

      {openTrend && loaded && (
        <MetricTrendDialog
          scope={scope}
          metric={{
            name: openTrend.name,
            aggregation: aggregations[openTrend.name] ?? 'total',
          }}
          currencySymbol={loaded.currencySymbol}
          onClose={() => setTrendMetric(null)}
        />
      )}
    </Box>
  );
}
